import {
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    GoogleAuthProvider,
    signInWithPopup
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "./firebase";

/**
 * AuthService: Centraliza toda a lógica de autenticação e perfil do NeoApp.
 * NÃO armazena estado no localStorage. O estado é gerido pelo AuthContext via onAuthStateChanged.
 */
const AuthService = {
    /**
     * Realiza login simples por e-mail e senha
     */
    login: async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Busca o perfil detalhado no Firestore
            const profile = await AuthService.getUserProfile(user.uid);

            // Trava de segurança no Member-Side para impedir acessos bloqueados
            if (profile && profile.status === 'inactive') {
                await signOut(auth);
                throw new Error("Seu acesso foi bloqueado pelo administrador. Contate o suporte.");
            }

            return user;
        } catch (error) {
            console.error("Erro detalhado no login:", error);
            throw error;
        }
    },

    /**
     * Realiza logout completo do Firebase e limpa os dados locais
     */
    logout: async () => {
        try {
            await signOut(auth);
            return true;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Solicita redefinição de senha e envia e-mail ao usuário
     */
    forgetPassword: async (email) => {
        const actionCodeSettings = {
            url: `${window.location.protocol}//${window.location.host}/login`,
        };
        try {
            await sendPasswordResetEmail(auth, email, actionCodeSettings);
            return true;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Busca os dados do perfil na coleção global 'users'
     */
    getUserProfile: async (uid) => {
        try {
            const userDocRef = doc(db, "users", uid);
            const userSnap = await getDoc(userDocRef);
            if (userSnap.exists()) {
                return userSnap.data();
            }
            return null;
        } catch (error) {
            console.error("Erro ao buscar perfil:", error);
            return null;
        }
    },

    /**
     * Realiza login via provedores sociais (Google)
     */
    socialLogin: async (type) => {
        let provider;
        if (type === "google") {
            provider = new GoogleAuthProvider();
        }

        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            // Busca ou inicializa o perfil após login social
            let profile = await AuthService.getUserProfile(user.uid);

            // Se o perfil não existe, podemos inicializar um documento básico (Opcional)
            if (!profile) {
                const profileData = {
                    uid: user.uid,
                    fullName: user.displayName,
                    email: user.email,
                    role: 'employee', // Cargo padrão moderado
                    status: 'active',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };
                await setDoc(doc(db, "users", user.uid), profileData);
                profile = profileData;
            }

            // Trava de segurança no Member-Side para impedir acessos bloqueados
            if (profile && profile.status === 'inactive') {
                await signOut(auth);
                throw new Error("Seu acesso foi bloqueado pelo administrador. Contate o suporte.");
            }

            return user;
        } catch (error) {
            throw error;
        }
    }
};

export default AuthService;
